import { Command } from 'cmdk';
import { Dialog as D } from 'radix-ui';
import { Briefcase, FolderPlus, Inbox, ListPlus, LogOut, Search, UserPlus, UserRound } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { hrefFor, SEARCH_LABELS, useSearch } from '@/lib/search';
import { useNavigate } from 'react-router';
import { create } from 'zustand';
import { useQueryClient } from '@tanstack/react-query';
import { useClients } from '@/lib/clients';
import { useProjects } from '@/lib/projects';
import { logout, useMe } from '@/lib/session';
import { SETTINGS_ITEM, visibleNav } from './nav';
import { useNavigationClient } from './client-navigation';
import { useCreateDialog } from './create-store';

export const usePalette = create<{ open: boolean; setOpen: (o: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

/**
 * ⌘K / Ctrl+K (SPEC §40 e §41): navegação, ações rápidas e busca em todo o estúdio
 * (projetos, clientes, tarefas, arquivos, boards, storyboards, notas, comentários e faturas).
 */
export function CommandPalette() {
  const { clientId } = useNavigationClient();
  const { open, setOpen } = usePalette();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const showCreate = useCreateDialog((st) => st.show);
  const [query, setQuery] = useState('');
  const results = useSearch(open ? query : '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!usePalette.getState().open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    navigate(hrefFor(to));
  };

  const pages = visibleNav(me.permissions).flatMap((g) => g.items);

  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-black/55 data-[state=open]:animate-in" />
        <D.Content className="fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-[18px] bg-raised shadow-float data-[state=open]:animate-pop">
          <D.Title className="sr-only">Comandos</D.Title>
          <D.Description className="sr-only">Navegue ou execute uma ação</D.Description>
          <Command label="Comandos" loop>
            <div className="flex items-center gap-3 border-b border-line px-4">
              <Search className="size-4 text-fg-subtle" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar ou ir para…"
                className="h-13 flex-1 bg-transparent text-[15px] outline-none placeholder:text-fg-subtle"
              />
            </div>
            <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-8 text-center text-[13.5px] text-fg-muted">
                {query.trim().length >= 2 && results.isFetching ? 'Buscando…' : 'Nada encontrado.'}
              </Command.Empty>
              {query.trim().length >= 2 &&
                results.data?.groups.map((g) => (
                  <Group key={g.group} heading={SEARCH_LABELS[g.group]}>
                    {g.hits.map((h) => (
                      <Row
                        key={`${g.group}:${h.id}`}
                        value={`${g.group}:${h.id} ${h.title}`}
                        icon={<Search />}
                        keywords={[query]}
                        onSelect={() => go(h.href)}
                      >
                        {h.title}
                        {h.subtitle && <span className="ml-1 text-fg-subtle">· {h.subtitle}</span>}
                      </Row>
                    ))}
                  </Group>
                ))}
              {query.trim().length >= 2 && (
                <Group heading="Busca">
                  <Row
                    icon={<Search />}
                    keywords={[query]}
                    onSelect={() => go(`/search?q=${encodeURIComponent(query.trim())}`)}
                  >
                    Ver todos os resultados para “{query.trim()}”
                  </Row>
                </Group>
              )}
              <Group heading="Ir para">
                {pages.map((p) => (
                  <Row
                    key={p.to}
                    icon={<p.icon />}
                    onSelect={() => go(p.children?.[0]?.to ?? p.to)}
                    keywords={p.children?.map((c) => c.label)}
                  >
                    {p.label}
                  </Row>
                ))}
                {pages.flatMap((p) =>
                  (p.children ?? []).map((c) => (
                    <Row key={c.to} icon={<p.icon />} onSelect={() => go(c.to)}>
                      {p.label} <span className="text-fg-subtle">/ {c.label}</span>
                    </Row>
                  )),
                )}
                <Row icon={<SETTINGS_ITEM.icon />} onSelect={() => go('/settings')}>
                  Configurações
                </Row>
              </Group>
              {open && <ProjectsGroup go={go} />}
              {open && <ClientsGroup go={go} />}
              <Group heading="Ações">
                {me.permissions.includes('tasks:write') && (
                  <Row icon={<ListPlus />} onSelect={() => (setOpen(false), showCreate('task'))}>
                    Nova tarefa
                  </Row>
                )}
                <Row icon={<Inbox />} onSelect={() => (setOpen(false), showCreate('request'))}>
                  Nova solicitação
                </Row>
                {me.permissions.includes('projects:create') && (
                  <Row
                    icon={<FolderPlus />}
                    onSelect={() => go(clientId ? `/clients/${clientId}/projects?new=1` : '/clients')}
                  >
                    Novo projeto
                  </Row>
                )}
                {me.permissions.includes('clients:write') && (
                  <Row icon={<Briefcase />} onSelect={() => go('/clients?new=1')}>
                    Novo cliente
                  </Row>
                )}
                {me.permissions.includes('members:invite') && (
                  <Row icon={<UserPlus />} onSelect={() => go('/settings/members?invite=1')}>
                    Convidar membro
                  </Row>
                )}
                <Row icon={<UserRound />} onSelect={() => go('/settings/account')}>
                  Editar minha conta
                </Row>
                <Row
                  icon={<LogOut />}
                  onSelect={async () => {
                    setOpen(false);
                    await logout().catch(() => {});
                    qc.clear();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sair
                </Row>
              </Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

function ProjectsGroup({ go }: { go: (to: string) => void }) {
  const projects = useProjects({ status: 'open' });
  if (!projects.data?.length) return null;
  return (
    <Group heading="Projetos">
      {projects.data.map((p) => (
        <Row
          key={p.id}
          icon={
            <span
              className="block size-2.5 rounded-[3px]"
              style={{ background: `var(--color-label-${p.color})` }}
            />
          }
          onSelect={() => go(`/projects/p/${p.id}`)}
          keywords={[p.client?.name ?? '']}
        >
          {p.name}
          {p.client && <span className="ml-2 text-fg-subtle">{p.client.name}</span>}
        </Row>
      ))}
    </Group>
  );
}

/** Clientes visíveis para a pessoa. A busca universal (projetos, tarefas, arquivos) chega na Fase 20. */
function ClientsGroup({ go }: { go: (to: string) => void }) {
  const clients = useClients();
  if (!clients.data?.length) return null;
  return (
    <Group heading="Clientes">
      {clients.data.map((c) => (
        <Row
          key={c.id}
          icon={
            <span
              className="block size-2.5 rounded-full"
              style={{ background: `var(--color-label-${c.color})` }}
            />
          }
          onSelect={() => go(`/clients/${c.id}`)}
          keywords={[c.brandName ?? '']}
        >
          {c.name}
        </Row>
      ))}
    </Group>
  );
}

function Group({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[12px] [&_[cmdk-group-heading]]:text-fg-subtle"
    >
      {children}
    </Command.Group>
  );
}

function Row({
  icon,
  children,
  onSelect,
  keywords,
  value,
}: {
  icon: ReactNode;
  children: ReactNode;
  onSelect: () => void;
  keywords?: string[] | undefined;
  value?: string;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      {...(value ? { value } : {})}
      keywords={keywords}
      className="flex h-10 cursor-default items-center gap-3 rounded-[10px] px-2.5 text-[14px] data-[selected=true]:bg-hover [&_svg]:size-4 [&_svg]:text-fg-muted"
    >
      {icon}
      <span className="flex-1 truncate">{children}</span>
    </Command.Item>
  );
}
